import type { OrderDto } from '../../../dto/order';
import {
  buildRecipientFieldsFromCustomer,
  type WZPrefillForOrderDto,
  type WZPrefillItemDto,
} from '../../../dto/wz';
import api from '../../../services/api';
import type { TFunction } from 'i18next';
import { submitWithOperationalException } from '../../operationalExceptions/operationalExceptionFlow';

import {
  fetchOrderDetail,
  fetchOrders,
  fetchWZPrefillForOrder,
} from '../../orders/api/ordersApi';
import type { WZConversionUnit } from '../../../utils/dimensionConversion';
import { normalizeConversionUnit } from '../../../utils/dimensionConversion';
import { normalizeUnitCode } from '../../../utils/units';

type OperationalExceptionModal = Parameters<typeof submitWithOperationalException>[2];

const WZ_ELIGIBLE_STATUSES = new Set([
  'CONFIRMED',
  'IN_FULFILLMENT',
  'READY_FOR_DISPATCH',
  'IN_DELIVERY',
  'PARTIALLY_DELIVERED',
  'DELIVERED',
]);

export interface WZCreateItemDraft {
  productVariantId?: string;
  sourceOrderProductId?: string | null;
  productName: string;
  productCode?: string;
  quantity: number;
  quantityPcs?: number | null;
  quantityM2?: number | null;
  quantityM3?: number | null;
  unit: string;
  conversionUnit?: WZConversionUnit | null;
  conversionQuantity?: number;
  saleQuantity?: number | null;
  saleUnit?: string | null;
  width?: number;
  height?: number;
  length?: number;
  thickness?: number;
}

export interface WZCreateHandoverValues {
  issuedBy: string;
  vehicleRegistration?: string;
  receivedBy?: string;
  notes?: string;
  deliveryAddress?: string;
  issueDate?: string;
  receivedDate?: string;
  salesInvoiceNumber?: string | null;
}

interface WZCreatePayloadInput {
  order: OrderDto;
  items: WZCreateItemDraft[];
  handover: WZCreateHandoverValues;
  issueDate: string;
  releaseDate: string;
  receivedDate: string;
}

export interface WZCreateResult {
  id?: string;
  /**
   * Server-assigned document number. Needed because the create confirmation
   * names the document ("Utworzono WZ/53/08/2026") rather than saying "Zapisano"
   * — and the operator usually left the number field blank, so this response is
   * the only place it exists.
   */
  wzNumber?: string;
}

/**
 * A customer-order lifecycle status is guidance for the operator, not a WZ
 * permission boundary. Quotes remain excluded because they are not real
 * customer orders yet; every real order stays selectable and a non-standard
 * lifecycle stage is surfaced as a warning in the picker/form.
 */
export function isWZEligibleOrder(order: Pick<OrderDto, 'isQuote'>): boolean {
  return order.isQuote !== true;
}

export function hasNonstandardWZOrderStatus(order: Pick<OrderDto, 'status'>): boolean {
  return !WZ_ELIGIBLE_STATUSES.has(order.status);
}

export function mapWZPrefillItem(item: WZPrefillItemDto): WZCreateItemDraft {
  const physicalUnit = normalizeUnitCode(item.unit) || 'pcs';
  const saleUnit = normalizeUnitCode(item.saleUnit);
  return {
    productVariantId: item.productVariantId || undefined,
    sourceOrderProductId: item.sourceOrderProductId ?? null,
    productName: item.productName || '',
    productCode: item.productCode || '',
    quantity: item.quantity || 1,
    quantityPcs: item.quantityPcs,
    quantityM2: item.quantityM2,
    quantityM3: item.quantityM3,
    unit: physicalUnit,
    conversionUnit: normalizeConversionUnit(item.conversionUnit),
    conversionQuantity: item.conversionQuantity,
    saleQuantity: item.saleQuantity ?? null,
    saleUnit: saleUnit ?? null,
    width: item.width ?? undefined,
    height: item.height ?? undefined,
    length: item.length ?? undefined,
    thickness: item.thickness ?? undefined,
  };
}

export function buildManualWZCreatePayload({
  order,
  items,
  handover,
  issueDate,
  releaseDate,
  receivedDate,
}: WZCreatePayloadInput) {
  const recipient = buildRecipientFieldsFromCustomer(order.customer);

  return {
    orderId: order.id,
    issueDate,
    releaseDate,
    ...recipient,
    deliveryAddress: handover.deliveryAddress?.trim() || recipient.recipientAddress,
    receivedDate,
    issuedBy: handover.issuedBy.trim(),
    receivedBy: handover.receivedBy?.trim() || '',
    vehicleRegistration: handover.vehicleRegistration?.trim() || '',
    notes: handover.notes?.trim() || '',
    salesInvoiceNumber: handover.salesInvoiceNumber?.trim() || '',
    items: items.map((item) => ({
      productVariantId: item.productVariantId,
      sourceOrderProductId: item.sourceOrderProductId ?? null,
      productName: item.productName,
      productCode: item.productCode,
      quantity: item.quantity,
      unit: item.unit,
      conversionUnit: item.conversionUnit,
      conversionQuantity: item.conversionQuantity,
      saleQuantity: item.saleQuantity,
      saleUnit: item.saleUnit,
      width: item.width,
      height: item.height,
      length: item.length,
      thickness: item.thickness,
    })),
  };
}

export async function fetchWZCreateOrderOptions(): Promise<OrderDto[]> {
  const orders = await fetchOrders({}, 'wz.new.orderOptions');
  return orders.filter(isWZEligibleOrder);
}

export function fetchWZCreateOrder(orderId: string): Promise<OrderDto> {
  return fetchOrderDetail(orderId, 'wz.new.order');
}

export function fetchWZCreatePrefill(orderId: string): Promise<WZPrefillForOrderDto> {
  return fetchWZPrefillForOrder(orderId);
}

/**
 * `confirmation` wires the shared operational-exception handshake
 * (WZ_VARIANT_DIFFERS_FROM_ORDER at create time): the operator confirms with a
 * reason, or cancels — in which case `null` is returned and nothing was saved.
 */
export async function createManualWZ(
  payload: ReturnType<typeof buildManualWZCreatePayload>,
  confirmation?: { modal: OperationalExceptionModal; t: TFunction },
): Promise<WZCreateResult | null> {
  if (!confirmation) {
    const response = await api.post('/wz', payload);
    return response.data;
  }
  const response = await submitWithOperationalException(
    (body) => api.post('/wz', body),
    payload as unknown as Record<string, unknown>,
    confirmation.modal,
    confirmation.t,
  );
  return response ? response.data : null;
}

export async function generateFullyCoveredWZ(
  orderId: string,
  handover: WZCreateHandoverValues,
  confirmFullyCovered = false,
): Promise<WZCreateResult> {
  const response = await api.post(`/wz/generate/${orderId}`, {
    issuedBy: handover.issuedBy.trim(),
    receivedBy: handover.receivedBy?.trim() || '',
    vehicleRegistration: handover.vehicleRegistration?.trim() || '',
    notes: handover.notes?.trim() || '',
    salesInvoiceNumber: handover.salesInvoiceNumber?.trim() || '',
    deliveryAddress: handover.deliveryAddress?.trim() || '',
    issueDate: handover.issueDate,
    releaseDate: handover.issueDate,
    receivedDate: handover.receivedDate,
    ...(confirmFullyCovered ? { confirmFullyCovered: true } : {}),
  });
  return response.data;
}
